import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { AlertCircle } from 'lucide-react';

export interface InputProps {
  label?: string;
  error?: string;
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
  multiline?: boolean;
  rows?: number;
  className?: string;
  id?: string;
  name?: string;
  value?: string;
  onChange?: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  onFocus?: (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  onBlur?: (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  placeholder?: string;
  type?: string;
  autoComplete?: string;
  disabled?: boolean;
  required?: boolean;
}

const Input = React.forwardRef<HTMLInputElement | HTMLTextAreaElement, InputProps>(
  (
    {
      label,
      error,
      leftIcon,
      rightIcon,
      multiline = false,
      rows = 4,
      className = '',
      id,
      name,
      value,
      onChange,
      placeholder,
      type = 'text',
      autoComplete,
      disabled,
      required,
      onFocus: propOnFocus,
      onBlur: propOnBlur,
    },
    ref
  ) => {
    const [isFocused, setIsFocused] = useState(false);
    const inputId = id || `input-${Math.random().toString(36).substr(2, 9)}`;

    const baseStyles =
      'w-full bg-dark-800/50 backdrop-blur-sm border-2 border-dark-700 text-dark-50 rounded-lg px-4 py-3 transition-all duration-300 focus:outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20 placeholder:text-dark-500';

    const errorStyles = error ? 'border-red-500 focus:border-red-500 focus:ring-red-500/20' : '';

    const InputComponent = multiline ? 'textarea' : 'input';

    return (
      <div className="w-full">
        {label && (
          <motion.label
            htmlFor={inputId}
            className="block text-sm font-medium text-dark-300 mb-2"
            animate={{
              color: isFocused ? '#38bdf8' : error ? '#ef4444' : '#cbd5e1',
            }}
            transition={{ duration: 0.2 }}
          >
            {label}
          </motion.label>
        )}

        <div className="relative">
          {leftIcon && (
            <div className="absolute left-3 top-1/2 -translate-y-1/2 text-dark-500">
              {leftIcon}
            </div>
          )}

          <motion.div
            animate={{
              scale: isFocused ? 1.01 : 1,
            }}
            transition={{ duration: 0.2 }}
          >
            <InputComponent
              ref={ref as any}
              id={inputId}
              name={name}
              value={value}
              onChange={onChange}
              placeholder={placeholder}
              type={multiline ? undefined : type}
              autoComplete={autoComplete}
              disabled={disabled}
              required={required}
              className={`${baseStyles} ${errorStyles} ${leftIcon ? 'pl-10' : ''} ${
                rightIcon || error ? 'pr-10' : ''
              } ${multiline ? 'resize-none' : ''} ${className}`}
              onFocus={(e) => {
                setIsFocused(true);
                propOnFocus?.(e);
              }}
              onBlur={(e) => {
                setIsFocused(false);
                propOnBlur?.(e);
              }}
              rows={multiline ? rows : undefined}
            />
          </motion.div>

          {(rightIcon || error) && (
            <div className="absolute right-3 top-1/2 -translate-y-1/2">
              {error ? (
                <AlertCircle className="text-red-500" size={20} />
              ) : (
                <span className="text-dark-500">{rightIcon}</span>
              )}
            </div>
          )}

          {/* Focus glow effect */}
          <AnimatePresence>
            {isFocused && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="absolute inset-0 rounded-lg bg-primary-500/5 -z-10 blur-xl"
              />
            )}
          </AnimatePresence>
        </div>

        <AnimatePresence>
          {error && (
            <motion.p
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              className="mt-2 text-sm text-red-500 flex items-center gap-1"
            >
              <AlertCircle size={14} />
              {error}
            </motion.p>
          )}
        </AnimatePresence>
      </div>
    );
  }
);

Input.displayName = 'Input';

export default Input;
